import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { verifyName } from "../services/api";
import type { SessionInfo } from "../services/api";
import axios from "axios";

interface AuthGateProps {
  sessionUuid: string;
  onVerified: (sessionInfo: SessionInfo) => void;
}

const AuthGate: React.FC<AuthGateProps> = ({ sessionUuid, onVerified }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setError(null);
    setIsLoading(true);
    try {
      const sessionInfo = await verifyName(sessionUuid, name.trim());
      if (!sessionInfo.aiMentReady) {
        setError(t("mentNotReady"));
        return;
      }
      onVerified(sessionInfo);
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 410) {
          setError(t("sessionExpiredLink"));
        } else if (err.response?.status === 401) {
          setError(t("nameMismatch"));
        } else if (err.response?.status === 404) {
          setError(t("sessionNotFound"));
        } else {
          setError(t("verifyFailed"));
        }
      } else {
        setError(t("verifyFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-gate-wrapper">
      <div className="auth-gate-container">
        <h2>{t("authGateTitle")}</h2>
        <p>{t("authGateDescription")}</p>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("authGateNamePlaceholder")}
            disabled={isLoading}
            autoFocus
          />
          {error && <p className="auth-gate-error">{error}</p>}
          <button type="submit" disabled={isLoading || !name.trim()}>
            {isLoading ? t("loading") : t("authGateSubmit")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AuthGate;
